import React, {useEffect, useState} from "react"
import {IonContent, IonPage, IonModal} from "@ionic/react"
import OrientationRoadTest from "./OrientationRoadTest"
import {ApiService} from "../api/ApiService";
import {OrientationDriverList} from "../api/model/OrientationDriverList";
import {Link} from "react-router-dom";
import LoadDetailHeader, {useTransparentHeaderContrast} from '../components/LoadDetailHeader';
import {ChevronRight, CloseIcon, SeatedIcon} from '../components/icons';
import { useLogPageVisit } from '../utils/logPageVisit';

const OrientationDrivers: React.FC = () => {
    useLogPageVisit('LP Road Tests', 'User viewed the Road Tests page');
    const {onSheet, onIonScroll} = useTransparentHeaderContrast();
    const [mounted, setMounted] = useState(false);

    const [startTime, setStartTime] = useState(new Date().toJSON().slice(0, 10));
    const [endTime, setEndTime] = useState(new Date(new Date().getTime() + 86400000).toJSON().slice(0, 10));
    const [drivers, setDrivers] = useState<OrientationDriverList[]>([]);
    const [showRoadTest, setShowRoadTest] = useState(false);
    const [driverName, setDriverName] = useState("Unknown");
    const [driverId, setDriverId] = useState("");
    const [terminal, setTerminal] = useState(localStorage.getItem("terminal") ?? "NAS");

    useEffect(() => {
      const id = requestAnimationFrame(() => {
        setTimeout(() => setMounted(true), 30);
      });
      return () => cancelAnimationFrame(id);
    }, []);

    useEffect(() => {
        getOrientationDrivers(terminal, startTime, endTime);
    }, []);

    const getOrientationDrivers = (terminal: string, startTime: string, endTime: string) => {
        ApiService.getOrientationDriverList("orientation", {
            disqualified: 0,
            startTime: startTime.slice(0, 10),
            endTime: endTime.slice(0, 10),
            start: 0,
            length: 1000,
            terminal: terminal
        }).then(it => setDrivers(it));
    }

    const selectDriver = (name: string, id: string) => {
        setDriverName(name);
        setDriverId(id);
    }

    const handleStartTimeChange = (time: string) => {
        setStartTime(time);
        setDrivers([]);
        getOrientationDrivers(terminal, time, endTime);
    }

    const handleEndTimeChange = (time: string) => {
        setEndTime(time);
        setDrivers([]);
        getOrientationDrivers(terminal, startTime, time);
    }

    const handleTerminal = (terminal: string) => {
        if (localStorage.getItem('terminal') === null) {
            localStorage.setItem('terminal', terminal);
        }
        setTerminal(terminal);
        setDrivers([]);
        getOrientationDrivers(terminal, startTime, endTime);
    }

    function closeRoadTest() {
        setShowRoadTest(false);
        window.location.reload();
    }

    function resetTerminal() {
        localStorage.removeItem('terminal');
        setTerminal('NAS');
    }

    const goBack = () => {
        if (window.history.length > 1) window.history.back();
        else window.location.replace('/Home');
    };

    return (
        <IonPage>
            <LoadDetailHeader title="Orientation" onBack={goBack} onSheet={onSheet} />

            <IonContent
              fullscreen
              className="design-a-content design-a-content--light"
              scrollEvents={true}
              onIonScroll={onIonScroll}
            >
              <div className={`design-a design-a--detail${mounted ? ' design-a--mounted' : ''}`}>
                <div className="home-hero advance-hero">
                  <div className="advance-hero__eyebrow">Orientation</div>
                  <div className="advance-hero__title home-display">Road Tests</div>
                  <div className="advance-hero__meta">
                    {drivers.length} driver{drivers.length === 1 ? '' : 's'} available
                  </div>
                </div>

                <div className="home-sheet advance-sheet">
                  <label className="profile-field">
                    <span className="profile-field__label">Select Terminal</span>
                    <select
                      id="state"
                      className="profile-field__input"
                      value={terminal}
                      onChange={e => handleTerminal(e.target.value!)}
                    >
                      <option value="VIR">VIR</option>
                      <option value="PLA">PLA</option>
                      <option value="NAZ">NAZ</option>
                      <option value="NAS">NAS</option>
                      <option value="FON">FON</option>
                      <option value="BTW">BTW</option>
                      <option value="AHO">AHO</option>
                    </select>
                  </label>

                  <div style={{display: 'flex', gap: '8px'}}>
                    <label className="profile-field" style={{flex: 1}}>
                      <span className="profile-field__label">From</span>
                      <input
                        className="profile-field__input"
                        type="date"
                        value={startTime}
                        onChange={e => handleStartTimeChange(e.target.value!.slice(0, 10))}
                      />
                    </label>
                    <label className="profile-field" style={{flex: 1}}>
                      <span className="profile-field__label">To</span>
                      <input
                        className="profile-field__input"
                        type="date"
                        value={endTime}
                        onChange={e => handleEndTimeChange(e.target.value!.slice(0, 10))}
                      />
                    </label>
                  </div>

                  <div className="home-section-label">Available Drivers</div>
                  {drivers.length > 0 ? (
                    <div className="training-trainee-list">
                      {drivers.map((driver, index) => (
                        <div className="training-trainee-row" key={index}>
                          <button
                            type="button"
                            className="home-action"
                            onClick={() => {
                              selectDriver(driver.name, driver.governmentId);
                              setShowRoadTest(true)
                            }}
                          >
                            <span className="home-action__icon">
                              <SeatedIcon />
                            </span>
                            <span className="home-action__label">{driver.name}</span>
                            <ChevronRight />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="scoreboard-empty">
                      <strong>No Drivers Available</strong>
                      <p>No drivers available for evaluation.</p>
                    </div>
                  )}

                  <div className="home-card training-info-card">
                    <div className="training-info-card__label">Manage the list you see here</div>
                    <p className="training-info-card__body">
                      If you were expecting drivers for the dates selected, contact your supervisor
                      to assist you.
                    </p>
                    <button type="button" className="profile-submit" onClick={() => resetTerminal()}>
                      Reset Terminal
                    </button>
                    <Link
                      to="/chainofcommand"
                      style={{
                        display: 'block',
                        marginTop: '10px',
                        textAlign: 'center',
                        fontFamily: 'var(--home-font-body)',
                        fontWeight: 600,
                        color: 'var(--home-accent)',
                        textDecoration: 'none',
                      }}
                    >
                      Who's my Supervisor?
                    </Link>
                  </div>
                </div>
              </div>
            </IonContent>

            <IonModal isOpen={showRoadTest} cssClass="my-custom-class">
              <div className="design-a design-a--mounted">
                <div className="training-modal">
                  <div className="training-modal__header">
                    <span>Driver Evaluation Form</span>
                    <button
                      type="button"
                      className="modal-close-btn training-modal__close"
                      onClick={() => closeRoadTest()}
                      aria-label="Close"
                    >
                      <CloseIcon size={18} color="#1a1a1a" />
                    </button>
                  </div>
                  <OrientationRoadTest name={driverName} governmentId={driverId} hideTest={() => {
                      setShowRoadTest(false)
                  }} />
                </div>
              </div>
            </IonModal>
        </IonPage>
    )
}

export default OrientationDrivers
